import React, { useState } from "react";
import Modal from "./Modal";
import VariantSelectionModal from "./VariantSelectionModal";
import { clusterApi } from "../services/api";
import { VariantInfo } from "../types";
import { EnlargeClusterResponse, KeywordReplacementUsage } from "../services/api";
import { useClient } from "../contexts/ClientContext";
import "./AddKeywordsModal.css";
import { UpgradeRounded } from "@mui/icons-material";

interface AddKeywordsModalProps {
  isOpen: boolean;
  onClose: () => void;
  anchorKeyword: string;
  currentClusterSize: number;
  maxKeywordsPerClusterLimit: undefined | number | "inf";
  replacementUsage?: KeywordReplacementUsage | null;
  onSuccess: () => void;
  locationCode?: number;
  languageCode?: string;
}

type TabType = "ai" | "manual";

const AddKeywordsModal: React.FC<AddKeywordsModalProps> = ({
  isOpen,
  onClose,
  anchorKeyword,
  currentClusterSize,
  maxKeywordsPerClusterLimit,
  replacementUsage,
  onSuccess,
  locationCode = 2840,
  languageCode = "en",
}) => {
  const { currentClient } = useClient();
  const [activeTab, setActiveTab] = useState<TabType>("ai");
  const [temperature, setTemperature] = useState<string>("0.7");
  const [numberOfKeywords, setNumberOfKeywords] = useState<string>("5");
  const [manualKeywords, setManualKeywords] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [variantModalOpen, setVariantModalOpen] = useState(false);
  const [enlargeResponse, setEnlargeResponse] =
    useState<EnlargeClusterResponse | null>(null);
  const [selectedVariants, setSelectedVariants] = useState<VariantInfo[]>([]);

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setActiveTab("manual");
      setTemperature("0.7");
      setNumberOfKeywords("5");
      setManualKeywords("");
      setError("");
      setIsLoading(false);
    }
  }, [isOpen]);

  const replacementLimit = replacementUsage?.limit ?? null;
  const replacementUsed = replacementUsage?.used ?? 0;
  const replacementsRemaining =
    replacementLimit === null ? null : Math.max(0, replacementLimit - replacementUsed);
  const replacementsExhausted = replacementsRemaining === 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (activeTab === "ai") {
      // Validate AI parameters
      if (
        temperature &&
        (isNaN(Number(temperature)) ||
          Number(temperature) < 0 ||
          Number(temperature) > 1)
      ) {
        setError("Temperature must be a number between 0 and 1");
        return;
      }

      if (
        numberOfKeywords &&
        (isNaN(Number(numberOfKeywords)) ||
          Number(numberOfKeywords) < 1 ||
          !Number.isInteger(Number(numberOfKeywords)))
      ) {
        setError("Number of keywords must be a positive integer");
        return;
      }

      // Validate that target cluster size would be larger than current
      const additions = Number(numberOfKeywords) || 5;
      const targetClusterSize = currentClusterSize + additions;
      if (targetClusterSize <= currentClusterSize) {
        setError("Number of keywords must be greater than 0");
        return;
      }
      if (replacementLimit !== null && additions + replacementUsed > replacementLimit) {
        const remainingNow = Math.max(0, replacementLimit - replacementUsed);
        setError(`You can change at most ${replacementLimit} keywords per cluster per billing period. You have ${remainingNow} left and tried to add ${additions}.`);
        return;
      }
      setIsLoading(true);
      try {
        // Call enlargeCluster and show review modal
        const response = await clusterApi.enlargeCluster(currentClient, {
          anchor_keyword: anchorKeyword,
          target_cluster_size: targetClusterSize,
          temperature: Number(temperature) || 0.7,
          location_code: locationCode,
          language_code: languageCode,
        });
        setEnlargeResponse(response);
        // Delay opening the variant modal to prevent flashing
        setTimeout(() => {
          setVariantModalOpen(true);
        }, 50);
      } catch (error) {
        setError(
          error instanceof Error ? error.message : "Failed to generate keywords"
        );
      } finally {
        setIsLoading(false);
      }
      return;
    } else {
      // Validate manual keywords
      if (!manualKeywords.trim()) {
        setError("Please enter at least one keyword");
        return;
      }
      if (typeof maxKeywordsPerClusterLimit === "number") {
        const manualKeywordsToAdd = manualKeywords
          .split("\n")
          .map((k) => k.trim())
          .filter((k) => k.length > 0)
          .length
        if (manualKeywordsToAdd + currentClusterSize > maxKeywordsPerClusterLimit) {
          setError(`Your plan's Max Keywords/Cluster is ${maxKeywordsPerClusterLimit}. You only have ${maxKeywordsPerClusterLimit - currentClusterSize} keywords quota remaining!`)
          return;
        }
        if (replacementLimit !== null) {
          if (manualKeywordsToAdd + replacementUsed > replacementLimit) {
            const remainingNow = Math.max(0, replacementLimit - replacementUsed);
            setError(`You can change at most ${replacementLimit} keywords per cluster per billing period. You have ${remainingNow} left and tried to add ${manualKeywordsToAdd}.`)
            return;
          }
        }
      }
    }

    setIsLoading(true);
    try {
      const keywords = manualKeywords
        .split("\n")
        .map((k) => k.trim())
        .filter((k) => k.length > 0);
      await clusterApi.addKeywords(currentClient, {
        anchor_keyword: anchorKeyword,
        keywords: keywords,
        location_code: locationCode,
        language_code: languageCode,
      });
      onSuccess();
      // Delay close to prevent flashing
      setTimeout(() => {
        onClose();
      }, 50);
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Failed to add keywords"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle user confirmation of AI-generated keywords
  const handleVariantsProcessed = async (selected?: VariantInfo[]) => {
    if (!selected) {
      setError("No variants selected");
      return;
    }
    setVariantModalOpen(false);
    setIsLoading(true);
    try {
      await clusterApi.confirmEnlargeCluster(currentClient, {
        anchor_keyword: anchorKeyword,
        new_variants: selected,
        location_code: locationCode,
        language_code: languageCode,
      });
      onSuccess();
      // Delay close to prevent flashing
      setTimeout(() => {
        onClose();
      }, 50);
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Failed to confirm keywords"
      );
    } finally {
      setIsLoading(false);
      setEnlargeResponse(null);
    }
  };

  const handleClose = () => {
    setActiveTab("ai");
    setTemperature("0.7");
    setNumberOfKeywords("5");
    setManualKeywords("");
    setError("");
    setIsLoading(false);
    onClose();
  };

  const maxKeywordsPerClusterReached: boolean = typeof maxKeywordsPerClusterLimit === "number"
    ? currentClusterSize >= maxKeywordsPerClusterLimit
    : false // inf or undefined (loading)

  if (!isOpen) return null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={`Add Keywords to "${anchorKeyword}"`}
        maxWidth="600px"
      >
        <div className="add-kw-modal">
          <div className="add-kw-tabs">
            <button
              type="button"
              className={`add-kw-tab ${
                activeTab === "manual" ? "add-kw-tab-active" : ""
              }`}
              onClick={() => setActiveTab("manual")}
            >
              Manual Entry
            </button>
            <button
              type="button"
              className={`add-kw-tab ${
                activeTab === "ai" ? "add-kw-tab-active" : ""
              }`}
              onClick={() => setActiveTab("ai")}
            >
              AI Generated
            </button>
          </div>

          <form onSubmit={handleSubmit} className="add-kw-form">
            <div className="add-kw-content">
              {activeTab === "ai" ? (
                <>
                  <div className="add-kw-form-group">
                    <label
                      htmlFor="add-kw-temperature"
                      className="add-kw-form-label"
                    >
                      Temperature
                    </label>
                    <input
                      id="add-kw-temperature"
                      type="number"
                      step="0.1"
                      min="0"
                      max="1"
                      value={temperature}
                      onChange={(e) => {
                        setTemperature(e.target.value);
                        if (error) setError("");
                      }}
                      className="add-kw-form-input"
                      disabled={isLoading || maxKeywordsPerClusterReached || replacementsExhausted}
                    />
                    <span className="add-kw-field-hint">
                      Controls creativity (0.0 = conservative, 1.0 = creative).
                      Default: 0.7
                    </span>
                  </div>

                  <div className="add-kw-form-group">
                    <label
                      htmlFor="add-kw-number"
                      className="add-kw-form-label"
                    >
                      Number of Additional Keywords
                    </label>
                    <input
                      id="add-kw-number"
                      type="number"
                      min="1"
                      value={numberOfKeywords}
                      onChange={(e) => {
                        setNumberOfKeywords(e.target.value);
                        if (error) setError("");
                      }}
                      className="add-kw-form-input"
                      disabled={isLoading || maxKeywordsPerClusterReached || replacementsExhausted}
                    />
                    <span className="add-kw-field-hint">
                      Number of additional keywords to generate using AI.
                      Default: 5
                    </span>
                  </div>
                </>
              ) : (
                <div className="add-kw-form-group">
                  <label htmlFor="add-kw-manual" className="add-kw-form-label">
                    Keywords (one per line)
                  </label>
                  <textarea
                    id="add-kw-manual"
                    value={manualKeywords}
                    onChange={(e) => {
                      setManualKeywords(e.target.value);
                      if (error) setError("");
                    }}
                    placeholder="Enter keywords, one per line:&#10;keyword 1&#10;keyword 2&#10;keyword 3"
                    className="add-kw-form-textarea"
                    rows={8}
                    disabled={isLoading || maxKeywordsPerClusterReached || replacementsExhausted}
                  />
                </div>
              )}
            </div>

            {error && (
              <div className="add-kw-error-message text-center">
                {error}
                {(
                  error.includes("billing period") 
                  || error.toLowerCase().includes("plan limit reached")
                  || error.toLowerCase().includes
                  ('max keywords/cluster is')
                ) && (
                  <>
                    <br />
                    <a
                      href="/admin?tab=billing"
                      className="upgrade-plan-link"
                    >
                      Upgrade your plan for more keywords.
                    </a>
                  </>
                )}
              </div>
            )}
            {maxKeywordsPerClusterReached && (
              <div className="add-kw-error-message">
                You have reached your plan's limit on Max Keywords/Cluster ({currentClusterSize}/{maxKeywordsPerClusterLimit}).
              </div>
            )}
            {replacementsExhausted && (
              <div className="add-kw-error-message">
                You have reached the {replacementLimit} keyword-replacement limit for this cluster this billing period. Quota resets when your subscription renews.
              </div>
            )}
            {(maxKeywordsPerClusterReached || replacementsExhausted) && (
              <a 
                className="bg-my-purple mx-6 mb-4 px-4 py-3 rounded-full text-white text-center font-semibold btn btn-primary"
                href='/admin?tab=billing'
              >
                <UpgradeRounded />
                Upgrade your plan to add more keywords
              </a>
            )}

            <div className="add-kw-form-actions">
              <button
                type="button"
                onClick={handleClose}
                className="add-kw-btn add-kw-btn-secondary"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="add-kw-btn add-kw-btn-primary"
                disabled={
                  isLoading ||
                  (activeTab === "ai" ? false : !manualKeywords.trim()) ||
                  maxKeywordsPerClusterReached ||
                  replacementsExhausted
                }
              >
                {isLoading ? (
                  <>
                    <span className="add-kw-loading-spinner"></span>
                    {activeTab === "ai" ? "Generating..." : "Adding..."}
                  </>
                ) : (
                  "Add Keywords"
                )}
              </button>
            </div>
          </form>
        </div>
      </Modal>
      {variantModalOpen && enlargeResponse && (
        <VariantSelectionModal
          isOpen={variantModalOpen}
          onClose={() => setVariantModalOpen(false)}
          anchorKeyword={anchorKeyword}
          variants={enlargeResponse.variants}
          onSuccess={handleVariantsProcessed}
          mode="expand"
        />
      )}
    </>
  );
};

export default AddKeywordsModal;
